<template>
    <breadcrumbs :crumbs="crumbs" />
    <section class="wrap wrap-compact">
        <form
            id="contact-form"
            method="POST"
            class="text-left py-8 at1024:py-[92px]"
            :class="{ errors: errors }"
            @invalid="invalidate"
        >
            <h1 class="font-bold text-gray text-2xl at720:text-4xl mb-2">
                Apply
            </h1>
            <h2 class="font-bold text-2xl mt-8 at720:text-xl at1024:text-3xl">
                {{ article.title }}
            </h2>
            <span class="space-y-2 text-gray-400"
                >{{ article.metadata?.type }} /
                {{ article.metadata?.location }}</span
            >
            <p class="mb-4 mt-8">
                <strong>All Fields are required.</strong> Thank you for
                considering a career at SECLOCK. Please take a minute to fill
                out the following form. After you have completed your
                application, an email will be sent to you with confirmation of
                your application.
            </p>
            <fieldset :disabled="uploading || form.processing">
                <!--form inputs-->
                <ul>
                    <li
                        class="flex flex-col w-full at720:gap-4 at720:grid at720:grid-cols-2"
                    >
                        <div class="py-2">
                            <seclock-input
                                :animated="true"
                                :contrast="true"
                                error="Required"
                                @invalid="invalidate"
                                label="First Name"
                                type="text"
                                minlength="3"
                                required
                                v-model="form.first_name"
                            />
                        </div>
                        <div class="py-2">
                            <seclock-input
                                :animated="true"
                                :contrast="true"
                                error="Required"
                                @invalid="invalidate"
                                label="Last Name"
                                type="text"
                                minlength="3"
                                required
                                v-model="form.last_name"
                            />
                        </div>
                    </li>
                    <li
                        class="flex flex-col w-full at720:gap-4 at720:grid at720:grid-cols-2"
                    >
                        <div class="py-2">
                            <seclock-input
                                :animated="true"
                                :contrast="true"
                                error="Required"
                                type="email"
                                @invalid="invalidate"
                                label="Email"
                                required
                                v-model="form.email"
                            />
                        </div>
                        <div class="py-2">
                            <seclock-input
                                :animated="true"
                                :contrast="true"
                                error="Minimum required length is 10 digits."
                                type="tel"
                                @invalid="invalidate"
                                pattern="(1)?(-|.|\s)?[0-9]{3}(-|.|\s)?[0-9]{3}(-|.|\s)?[0-9]{4}"
                                label="Phone"
                                minlength="10"
                                required
                                v-model="form.phone"
                            />
                        </div>
                    </li>
                    <li
                        class="flex flex-col w-full at720:gap-4 at720:grid at720:grid-cols-2"
                    >
                        <div class="py-2">
                            <seclock-input
                                :animated="true"
                                :contrast="true"
                                error="Required"
                                type="text"
                                @invalid="invalidate"
                                label="City"
                                minlength="3"
                                required
                                v-model="form.city"
                            />
                        </div>
                        <div class="py-2">
                            <SeclockInput
                                type="select"
                                label="State"
                                v-model="form.state"
                                class="mb-[0.5em]"
                                :animated="true"
                                :contrast="true"
                            >
                                <option
                                    v-for="state in config.states"
                                    :value="state"
                                >
                                    {{ state }}
                                </option>
                            </SeclockInput>
                        </div>
                    </li>
                </ul>
                <div class="mb-2 mt-5">
                    <h4 class="text-gray text-xl"><b>Add Resume</b> (optional)</h4>
                    <p class="text-gray-400 text-sm">Upload a PDF, Word document, or image</p>
                </div>
                <file-upload
                    :patch_update_route="route('web.careers.apply.upload')"
                    max-file-size="2MB"
                />
                <div
                    class="border-t border-gray-150 flex flex-col gap-4 mt-8 pt-8 at768:flex-row at768:justify-between at768:items-center"
                >
                    <Link
                        :href="
                            route('web.careers.show', { article: article.slug })
                        "
                        class="btn-black-on-gray justify-center"
                        >Cancel</Link
                    >
                    <button
                        @click.stop.prevent="submit"
                        :disabled="form.processing"
                        class="btn-white-on-black justify-center"
                    >
                        Apply
                    </button>
                </div>
                <RecaptchaDisclaimer
                    class="mt-4 text-center text-gray text-sm at768:mt-8"
                />
            </fieldset>
        </form>
    </section>
    <Meta
        :meta="article.meta"
        :title="'APPLY – ' + article.title"
        :description="'Apply now for a ' + article.title + ' position at SECLOCK.'"
        :jsonLdMainEntity="jsonLdMainEntityBlogShow(article)"
        :jsonLdBreadcrumbs="crumbs"
    />
</template>
<script setup>
import { onMounted, onUnmounted, ref, inject } from "vue";
import { Meta, Breadcrumbs, SeclockInput } from "@/Components";
import { useForm } from "@inertiajs/vue3";
import { FileUpload, RecaptchaDisclaimer } from "@/Components";
import {
    useJsonLd,
    useAssetTransforms,
    useRecaptcha,
    eventBus,
} from "@/Composables";
import config from "@/config";

const route = inject("route");
const { getRecaptchaToken } = useRecaptcha();
const { marketingAssetUrl } = useAssetTransforms();
const props = defineProps(["article", "crumbs"]);
const { jsonLdMainEntityBlogShow } = useJsonLd();

const errors = ref(false);

const uploaded_files = ref([]);

const onUploadFile = (docs) => (uploaded_files.value = docs);
onMounted(() => eventBus.on("uploadFile:doc", onUploadFile));
onUnmounted(() => eventBus.off("uploadFile:doc", onUploadFile));

function invalidate() {
    errors.value = true;
}

const form = useForm({
    position: props.article.title,
    first_name: null,
    last_name: null,
    email: null,
    phone: null,
    city: null,
    state: "",
    docs: [],
    form_name: "career",
    recaptcha: null,
});

const submit = async (event) => {
    form.clearErrors();

    //ensure the uploaded and attached documents are included in the form submission.
    form.docs = uploaded_files.value;

    form.recaptcha = await getRecaptchaToken("career");

    form.post(route("web.sendgrid"), {
        preserveScroll: true,
        onSuccess: () => {
            form.reset();
        },
        onError: (response) => {
            let form_errors = Object.keys(response ?? []).filter(
                (key) => typeof key === "string" && key.indexOf("docs") < 0
            );

            if (form_errors.length) {
                eventBus.emit("alert:show", {
                    title: "Error",
                    message: "Required fields are missing.",
                    level: "danger",
                });
            }

            let upload_errors = Object.entries(response ?? [])
                .filter(
                    ([key, message]) =>
                        typeof key === "string" && key.indexOf("docs") > -1
                )
                .map(([key, message]) => message);

            if (upload_errors.length) {
                let unique_error_message = [...new Set(upload_errors)];
                unique_error_message.forEach((error) => {
                    eventBus.emit("alert:show", {
                        title: "Error",
                        message: error,
                        level: "danger",
                    });
                });
            }
        },
    });
};
</script>
